import React, { useState } from 'react';
import { DragDropContext } from 'react-beautiful-dnd';
import axios from 'axios';
import PosePanel from './PosePanel';
import SearchForm from './SearchForm';
import style from './MovePoses.module.sass';

const MovePoses = ({ studentImageId, studentName, studentId, season, poses: initialPoses }) => {
  const [leftPoses, setLeftPoses] = useState(initialPoses);
  const [rightPoses, setRightPoses] = useState([]);
  const [rightStudentImage, setRightStudentImage] = useState(null);
  const [leftBestPoseId, setLeftBestPoseId] = useState(
    () => {
      const best = initialPoses.find((p) => p.bestPose);
      return best ? best.id : null;
    }
  );
  const [rightBestPoseId, setRightBestPoseId] = useState(null);
  const [finishing, setFinishing] = useState(false);
  const [error, setError] = useState('');

  const handleRightLoad = (data) => {
    setRightStudentImage(data.studentImage);
    setRightPoses(data.poses);
    const best = data.poses.find((p) => p.bestPose);
    setRightBestPoseId(best ? best.id : null);
  };

  const handleDragEnd = (result) => {
    const { source, destination, draggableId } = result;
    if (!destination) return;
    if (source.droppableId === destination.droppableId) return;

    const poseId = parseInt(draggableId);
    const isMovingRight = source.droppableId === 'left' && destination.droppableId === 'right';
    const isMovingLeft = source.droppableId === 'right' && destination.droppableId === 'left';

    if (!isMovingRight && !isMovingLeft) return;
    if (isMovingRight && !rightStudentImage) return;

    const targetStudentImageId = isMovingRight ? rightStudentImage.id : studentImageId;
    const sourcePoses = isMovingRight ? leftPoses : rightPoses;
    const targetPoses = isMovingRight ? rightPoses : leftPoses;
    const setSource = isMovingRight ? setLeftPoses : setRightPoses;
    const setTarget = isMovingRight ? setRightPoses : setLeftPoses;

    const pose = sourcePoses.find((p) => p.id === poseId);
    if (!pose) return;

    // Optimistic update
    setSource(sourcePoses.filter((p) => p.id !== poseId));
    const newTargetPoses = [...targetPoses];
    newTargetPoses.splice(destination.index, 0, pose);
    setTarget(newTargetPoses);

    // Clear best pose selection if the moved pose was selected
    if (isMovingRight && leftBestPoseId === poseId) setLeftBestPoseId(null);
    if (isMovingLeft && rightBestPoseId === poseId) setRightBestPoseId(null);

    // Persist to server
    axios
      .post('/admin/student_images/move_pose', {
        pose_id: poseId,
        target_student_image_id: targetStudentImageId
      })
      .catch(() => {
        // Revert on failure
        setSource((prev) => [...prev, pose].sort((a, b) => a.id - b.id));
        setTarget((prev) => prev.filter((p) => p.id !== poseId));
        alert('Failed to move pose. Please try again.');
      });
  };

  const handleFinish = async () => {
    setError('');

    if (leftPoses.length > 0 && !leftBestPoseId) {
      setError('Please select a best pose for the left side.');
      return;
    }
    if (rightPoses.length > 0 && !rightBestPoseId) {
      setError('Please select a best pose for the right side.');
      return;
    }

    setFinishing(true);

    try {
      // Set best pose for left side
      await axios.post('/admin/student_images/set_best_pose', {
        student_image_id: studentImageId,
        pose_id: leftPoses.length > 0 ? leftBestPoseId : null
      });

      // Set best pose for right side (if loaded)
      if (rightStudentImage) {
        await axios.post('/admin/student_images/set_best_pose', {
          student_image_id: rightStudentImage.id,
          pose_id: rightPoses.length > 0 ? rightBestPoseId : null
        });
      }

      // Redirect to right-side student's images page (or left if no right side)
      const targetStudentId = rightStudentImage ? rightStudentImage.student_id : studentId;
      window.location = `/admin/student_images?student_id=${targetStudentId}`;
    } catch (e) {
      setError('Failed to set best pose. Please try again.');
      setFinishing(false);
    }
  };

  return (
    <div>
      <DragDropContext onDragEnd={handleDragEnd}>
        <div className={style.container}>
          <div className={style.side}>
            <PosePanel
              droppableId="left"
              title={studentName}
              subtitle={`Season: ${season}`}
              poses={leftPoses}
              selectedPoseId={leftBestPoseId}
              onSelectPose={setLeftBestPoseId}
            />
          </div>

          <div className={style.side}>
            <SearchForm onLoad={handleRightLoad} />
            {rightStudentImage && (
              <PosePanel
                droppableId="right"
                title={rightStudentImage.student_name}
                subtitle={`Season: ${rightStudentImage.season}`}
                poses={rightPoses}
                selectedPoseId={rightBestPoseId}
                onSelectPose={setRightBestPoseId}
              />
            )}
            {!rightStudentImage && (
              <div className={style.panel}>
                <div className={style.panelHeader}>
                  <h4>Target Student</h4>
                </div>
                <div className={style.dropZone}>
                  <div className={style.emptyState}>
                    Search for a student above to load their poses
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </DragDropContext>

      <div className={style.finishBar}>
        {error && <span className={style.error}>{error}</span>}
        <button
          className={`btn btn-success ${style.finishBtn}`}
          onClick={handleFinish}
          disabled={finishing}
        >
          {finishing ? 'Saving...' : 'Finish'}
        </button>
      </div>
    </div>
  );
};

export default MovePoses;
